import { makeAutoObservable, runInAction } from 'mobx'
import {
  BillingApiError,
  canManageBilling as entitlementsCanManageBilling,
  canUseWorkspace as entitlementsCanUseWorkspace,
  fetchBillingEntitlements,
  invalidateBillingEntitlementsCache,
  isEntitlementsEmailUnverified,
  isSubscriptionAccessAllowed,
  type BillingEntitlements,
} from '@/api/billingApi'
import { authStore } from '@store/AuthStore'
import { emailVerificationStore } from '@store/EmailVerificationStore'
import { clearCentrifugeTokenCache } from '@utils/centrifugeTokens'
import { t } from '@store/I18nStore'
import {
  clearHiddenNotices,
  daysUntil,
  deriveExpiringNotice,
  hasSeenExpiredPromptThisSession,
  hideNoticeForADay,
  isNoticeHidden,
  isNoticeSuppressedForEntitlements,
  markExpiredPromptSeen,
  mergeNotices,
  parseSubscriptionNotice,
  type SubscriptionNotice,
} from '@utils/subscriptionNotice'

export type BillingRestrictionPayload = {
  code?: string | null
  reason?: string | null
  details?: unknown
}

export type BillingRefreshOutcome = 'ok' | 'error' | 'stale'

export type BillingAlertKind = 'none' | 'warning' | 'critical'

function reasonFromDetails(details: unknown, fallback?: string | null): string | null {
  if (details && typeof details === 'object') {
    const access = (details as { access?: { reason?: unknown } }).access
    if (typeof access?.reason === 'string' && access.reason.trim()) return access.reason
    const reason = (details as { reason?: unknown }).reason
    if (typeof reason === 'string' && reason.trim()) return reason
  }
  return fallback?.trim() || null
}

export class BillingAccessStore {
  entitlements: BillingEntitlements | null = null
  loading = false
  error: BillingApiError | null = null
  workspaceRestricted = false
  restrictionCode: string | null = null
  restrictionReason: string | null = null
  notice: SubscriptionNotice | null = null
  noticeHidden = false
  expiredPromptOpen = false
  /** Bumped to recreate the Centrifuge connection once on restrict / confirmed restore. */
  connectionEpoch = 0
  private loadKey = ''
  private loadPromise: Promise<BillingRefreshOutcome> | null = null
  /** Invalidates in-flight refresh() results. Session (loadKey) can stay the same. */
  private requestGen = 0

  constructor() {
    makeAutoObservable(this)
  }

  get canUseWorkspace(): boolean {
    if (this.workspaceRestricted) return false
    if (!this.entitlements) return false
    return entitlementsCanUseWorkspace(this.entitlements)
  }

  get canManageBilling(): boolean {
    return entitlementsCanManageBilling(this.entitlements)
  }

  /** Email confirmation takes priority over tariff restriction / trial start. */
  get awaitingEmailVerification(): boolean {
    if (authStore.currentUser?.emailVerified === true) return false
    if (emailVerificationStore.blocked) return true
    if (authStore.currentUser?.emailVerified === false) return true
    return isEntitlementsEmailUnverified(this.entitlements)
  }

  get subscriptionAccessAllowed(): boolean {
    if (this.workspaceRestricted && this.restrictionCode === 'SUBSCRIPTION_EXPIRED') return false
    return isSubscriptionAccessAllowed(this.entitlements)
  }

  /** True until the first entitlements result (or a restriction) arrives. */
  get accessCheckPending(): boolean {
    if (this.awaitingEmailVerification) return false
    if (this.workspaceRestricted) return false
    if (this.entitlements) return false
    if (this.error) return false
    return true
  }

  get billingAlert(): BillingAlertKind {
    if (this.awaitingEmailVerification) return 'none'
    if (!this.canManageBilling) return 'none'
    if (this.workspaceRestricted || this.notice?.level === 'critical') return 'critical'
    if (this.notice && isNoticeSuppressedForEntitlements(this.notice, this.entitlements)) return 'none'
    if (this.notice?.level === 'warning' && !this.noticeHidden) return 'warning'
    return 'none'
  }

  applyNotice(raw: unknown): void {
    if (this.awaitingEmailVerification) return
    const incoming = parseSubscriptionNotice(raw)
    if (!incoming) return
    if (isNoticeSuppressedForEntitlements(incoming, this.entitlements)) return
    if (incoming.level === 'critical' || incoming.code === 'T0') {
      this.applyExpired({
        code: 'SUBSCRIPTION_EXPIRED',
        details: raw,
      })
      return
    }
    this.notice = mergeNotices(this.notice, incoming)
    this.noticeHidden = false
  }

  applyExpired(payload: BillingRestrictionPayload = {}): void {
    if (this.awaitingEmailVerification) return
    const code = payload.code || 'SUBSCRIPTION_EXPIRED'
    if (code !== 'SUBSCRIPTION_EXPIRED') return
    const alreadyRestricted = this.workspaceRestricted && this.restrictionCode === 'SUBSCRIPTION_EXPIRED'
    this.invalidateInFlight()
    this.workspaceRestricted = true
    this.restrictionCode = code
    this.restrictionReason = reasonFromDetails(payload.details, payload.reason)
    this.notice = {
      code: 'T0',
      level: 'critical',
      expiresAt:
        parseSubscriptionNotice(payload.details)?.expiresAt ??
        this.notice?.expiresAt ??
        this.entitlements?.subscription?.currentPeriodEnd ??
        this.entitlements?.subscription?.accessUntil ??
        null,
      dismissible: false,
    }
    this.noticeHidden = false
    if (!alreadyRestricted && !hasSeenExpiredPromptThisSession()) {
      this.expiredPromptOpen = true
    }
    invalidateBillingEntitlementsCache()
    if (!alreadyRestricted) {
      this.connectionEpoch += 1
    }
  }

  dismissExpiredPrompt(): void {
    this.expiredPromptOpen = false
    markExpiredPromptSeen()
  }

  hideWarningNotice(): void {
    if (!this.notice || this.notice.level === 'critical' || !this.notice.dismissible) return
    hideNoticeForADay(this.notice.code)
    this.noticeHidden = true
  }

  async handleRenewed(
    apiBaseUrl: string,
    authHeaders: Record<string, string>,
    options?: { recreateConnection?: boolean },
  ): Promise<void> {
    const recreate = options?.recreateConnection !== false
    invalidateBillingEntitlementsCache()
    clearCentrifugeTokenCache()
    clearHiddenNotices()
    this.notice = null
    this.noticeHidden = false
    this.expiredPromptOpen = false
    this.workspaceRestricted = false
    this.restrictionCode = null
    this.restrictionReason = null
    if (recreate) {
      this.connectionEpoch += 1
    }
    await this.refresh(apiBaseUrl, authHeaders, { force: true, skipConnectionBump: true })
  }

  reset(): void {
    this.invalidateInFlight()
    this.entitlements = null
    this.error = null
    this.workspaceRestricted = false
    this.restrictionCode = null
    this.restrictionReason = null
    this.notice = null
    this.noticeHidden = false
    this.expiredPromptOpen = false
    this.loadKey = ''
    invalidateBillingEntitlementsCache()
  }

  async ensureLoaded(apiBaseUrl: string, authHeaders: Record<string, string>): Promise<void> {
    const base = apiBaseUrl.replace(/\/$/, '')
    if (!base) return
    const key = `${base}|${authHeaders.Authorization ?? ''}`
    if (this.loadKey === key) {
      if (this.loadPromise) {
        await this.loadPromise
        return
      }
      // Success or failure already settled for this session — do not refetch on re-render.
      if (this.entitlements || this.error) return
    }
    await this.refresh(apiBaseUrl, authHeaders)
  }

  async refresh(
    apiBaseUrl: string,
    authHeaders: Record<string, string>,
    options?: { force?: boolean; skipConnectionBump?: boolean },
  ): Promise<BillingRefreshOutcome> {
    const base = apiBaseUrl.replace(/\/$/, '')
    if (!base) return 'stale'
    const key = `${base}|${authHeaders.Authorization ?? ''}`
    if (!options?.force && this.loadPromise && this.loadKey === key) {
      return this.loadPromise
    }

    const gen = ++this.requestGen
    this.loading = true
    this.error = null
    this.loadKey = key

    const promise = (async (): Promise<BillingRefreshOutcome> => {
      try {
        const data = await fetchBillingEntitlements(base, authHeaders, { force: true })
        if (this.requestGen !== gen) return 'stale'
        runInAction(() => {
          if (this.requestGen !== gen) return
          const wasRestricted = this.workspaceRestricted
          this.entitlements = data
          this.error = null
          if (authStore.currentUser?.emailVerified === false || emailVerificationStore.blocked) {
            this.workspaceRestricted = false
            this.restrictionCode = null
            this.restrictionReason = data.access?.reason ?? null
            this.notice = null
            this.noticeHidden = false
            this.expiredPromptOpen = false
            return
          }
          if (entitlementsCanUseWorkspace(data) && isSubscriptionAccessAllowed(data)) {
            this.workspaceRestricted = false
            this.restrictionCode = null
            this.restrictionReason = data.access?.reason ?? null
          } else if (!entitlementsCanUseWorkspace(data) || !isSubscriptionAccessAllowed(data)) {
            this.workspaceRestricted = !entitlementsCanUseWorkspace(data)
            this.restrictionCode = isSubscriptionAccessAllowed(data) ? null : 'SUBSCRIPTION_EXPIRED'
            this.restrictionReason = data.access?.reason ?? this.restrictionReason
          }
          this.syncNoticeFromEntitlements(data)
          if (!options?.skipConnectionBump) {
            if (!wasRestricted && this.workspaceRestricted) {
              this.connectionEpoch += 1
            } else if (wasRestricted && this.canUseWorkspace) {
              this.connectionEpoch += 1
            }
          }
        })
        if (this.requestGen !== gen) return 'stale'
        return 'ok'
      } catch (err) {
        if (this.requestGen !== gen) return 'stale'
        runInAction(() => {
          if (this.requestGen !== gen) return
          this.entitlements = null
          this.error =
            err instanceof BillingApiError
              ? err
              : new BillingApiError(t('errors.billing.load_limits'), 500)
          if (
            !this.awaitingEmailVerification &&
            err instanceof BillingApiError &&
            err.status === 402 &&
            err.code === 'SUBSCRIPTION_EXPIRED'
          ) {
            this.applyExpired({
              code: err.code,
              reason: err.message,
              details: err.details,
            })
          }
        })
        if (this.requestGen !== gen) return 'stale'
        return 'error'
      } finally {
        runInAction(() => {
          if (this.requestGen === gen) this.loading = false
        })
      }
    })()

    this.loadPromise = promise
    try {
      return await promise
    } finally {
      if (this.loadPromise === promise) this.loadPromise = null
    }
  }

  private syncNoticeFromEntitlements(data: BillingEntitlements): void {
    if (isEntitlementsEmailUnverified(data) || this.awaitingEmailVerification) {
      this.notice = null
      this.noticeHidden = false
      return
    }
    if (this.workspaceRestricted || !isSubscriptionAccessAllowed(data)) {
      this.notice = mergeNotices(this.notice, {
        code: 'T0',
        level: 'critical',
        expiresAt: data.subscription?.currentPeriodEnd ?? data.subscription?.accessUntil ?? null,
        dismissible: false,
      })
      this.noticeHidden = false
      return
    }
    const derived = deriveExpiringNotice(data)
    if (derived) {
      this.notice = mergeNotices(this.notice?.level === 'critical' ? null : this.notice, derived)
      this.noticeHidden = isNoticeHidden(this.notice.code)
      return
    }
    if (this.notice && isNoticeSuppressedForEntitlements(this.notice, data)) {
      this.notice = null
      this.noticeHidden = false
      return
    }
    if (this.notice?.level === 'warning') {
      const days = daysUntil(this.notice.expiresAt)
      if (days == null || days > 3 || days <= 0) {
        this.notice = null
        this.noticeHidden = false
      }
    }
  }

  private invalidateInFlight(): void {
    this.requestGen += 1
    this.loadPromise = null
    this.loading = false
  }
}

export const billingAccessStore = new BillingAccessStore()
